import datetime from "config/datetime";
import { firstBy } from "thenby";
import type { LineData, LinePointData } from "components/charting/types/Chart.types";
import { MetadataType } from "types/metadata";
import type { OneRepMax, OneRepMaxValue } from "types/onerepmax";
import type { LoadVelocityProfileLite } from "types/load-velocity-profile";
import type { Palette } from "@mui/material/styles";
import { DeltaTypes } from "types/delta-types";
import type { DeltaType } from "types/delta-types";

/** Progress from the first value, coloured green, red and grey respectively. Mirrors Capture's ProgressBubbleState. */
export const ProgressBubbleEnum = {
  AboveBaseline: "AboveBaseline",
  BelowBaseline: "BelowBaseline",
  OnTrack: "OnTrack",
} as const;

export type ProgressBubbleState = (typeof ProgressBubbleEnum)[keyof typeof ProgressBubbleEnum];

export type OneRepMaxAggregationUnit = "day" | "week" | "month";

// Same as Capture's NEUTRAL_CHANGE_KILOGRAMS: a smaller change from the first value is no change.
const NEUTRAL_CHANGE_KILOGRAMS = 0.5;

export interface OneRepMaxProgressEntry {
  variant?: LineData["variant"];
  last: LinePointData;
  first: LinePointData | null;
  state: ProgressBubbleState;
}

class OneRepMaxChartHelpers {
  static toOneRepMaxChartData(oneRepMaxes: OneRepMax[], startDate: string, endDate: string): LineData[] {
    return oneRepMaxes.map((oneRepMax) => {
      const repeatedValues = {
        measure: {
          id: oneRepMax.exerciseId,
          metricField: "weight",
        },
        source: { id: oneRepMax.athleteId },
        entityId: oneRepMax.exerciseId,
      };

      return {
        ...repeatedValues,
        metadataType: MetadataType.ONE_REP_MAX,
        line: oneRepMax.oneRepMaxes
          .filter((oneRepMaxValue) => {
            const createdDate = datetime(oneRepMaxValue.createdDate);
            return createdDate.isAfter(startDate) && createdDate.isBefore(endDate);
          })
          .slice()
          .sort(firstBy("createdDate"))
          .map((oneRepMaxValue) => ({
            ...repeatedValues,
            metadataType: MetadataType.ONE_REP_MAX,
            x: datetime(oneRepMaxValue.createdDate).toDate(),
            y: oneRepMaxValue.weight,
            measurement: oneRepMaxValue,
            variant: oneRepMaxValue.variant ?? undefined,
          })),
      };
    });
  }

  // The profiles are already scoped to one athlete, one exercise and the selected date range.
  static toVelocityChartData(
    loadVelocityProfiles: LoadVelocityProfileLite[],
    athleteId: string,
    exerciseId: string
  ): LineData[] {
    const repeatedValues = {
      measure: {
        id: exerciseId,
        metricField: "weight",
      },
      source: { id: athleteId },
      entityId: exerciseId,
    };

    return [
      {
        ...repeatedValues,
        metadataType: MetadataType.LOAD_VELOCITY_PROFILE,
        line: loadVelocityProfiles
          .slice()
          .sort(firstBy("createdAt"))
          .map((loadVelocityProfile) => ({
            ...repeatedValues,
            metadataType: MetadataType.LOAD_VELOCITY_PROFILE,
            x: datetime(loadVelocityProfile.createdAt).toDate(),
            y: loadVelocityProfile.predictedOneRepMaxRange.max,
            measurement: loadVelocityProfile,
            variant: loadVelocityProfile.variant ?? undefined,
          })),
      },
    ];
  }

  // Mirrors the dashboard progress widgets, which bucket their x-axis by P7D, P30D, P90D and P365D.
  static getXAxisTickFormat(spanInDays: number) {
    return spanInDays > 90 ? "MMM" : "MMM D";
  }

  static getAggregationUnit(spanInDays: number): OneRepMaxAggregationUnit {
    if (spanInDays <= 31) return "day";
    if (spanInDays <= 90) return "week";
    return "month";
  }

  static getPeriodDateFormat(unit: OneRepMaxAggregationUnit) {
    if (unit === "day") return "dddd, LL";
    if (unit === "week") return "LL";
    return "MMMM, YYYY";
  }

  // Keeps the highest 1RM of each period, placed at the period's start like the dashboard windows.
  static groupLinesByPeriod(lines: LineData[], unit: OneRepMaxAggregationUnit): LineData[] {
    return lines.map((line) => {
      const bestByPeriod = new Map<number, LinePointData>();

      line.line.forEach((point) => {
        const periodStart = datetime(point.x).startOf(unit).valueOf();
        const best = bestByPeriod.get(periodStart);
        if (!best || point.y > best.y) {
          bestByPeriod.set(periodStart, { ...point, x: new Date(periodStart) });
        }
      });

      return { ...line, line: Array.from(bestByPeriod.values()).slice().sort(firstBy("x")) };
    });
  }

  static getRecordedDate(point: LinePointData) {
    if (point.metadataType === MetadataType.ONE_REP_MAX) return point.measurement.createdDate;
    if (point.metadataType === MetadataType.LOAD_VELOCITY_PROFILE) return point.measurement.createdAt;
    return point.x;
  }

  // Takes the plotted lines, so each value is its period's best and progress spans the first to the last point.
  // One entry per line, so left and right variants each keep their own progress across the chart.
  static getProgressEntries(lines: LineData[]): OneRepMaxProgressEntry[] {
    return lines
      .filter((line) => line.line.length)
      .map((line) => {
        const last = line.line[line.line.length - 1];
        const first = line.line.length > 1 ? line.line[0] : null;

        return {
          variant: line.variant,
          last,
          first,
          state: OneRepMaxChartHelpers.getProgressState(first?.y ?? null, last.y),
        };
      });
  }

  // As Capture's 1RM widget: any rise from the first value is above baseline, any fall below.
  static getProgressState(first: number | null, last: number): ProgressBubbleState {
    if (first === null) return ProgressBubbleEnum.OnTrack;

    const change = last - first;

    if (Math.abs(change) < NEUTRAL_CHANGE_KILOGRAMS) return ProgressBubbleEnum.OnTrack;
    return change > 0 ? ProgressBubbleEnum.AboveBaseline : ProgressBubbleEnum.BelowBaseline;
  }

  // The darker shade tells apart a side whose state an earlier side already has, as Capture's sideColors.
  static getProgressStateColor(state: ProgressBubbleState, palette: Palette, dark = false): string {
    switch (state) {
      case ProgressBubbleEnum.AboveBaseline:
        return dark ? palette.secondary1[180] : palette.secondary1[100];
      case ProgressBubbleEnum.BelowBaseline:
        return dark ? palette.secondary2[180] : palette.secondary2[100];
      default:
        return dark ? palette.grey[160] : palette.grey[100];
    }
  }

  static getProgressStateDeltaType(state: ProgressBubbleState): DeltaType {
    switch (state) {
      case ProgressBubbleEnum.AboveBaseline:
        return DeltaTypes.Increase;
      case ProgressBubbleEnum.BelowBaseline:
        return DeltaTypes.Decrease;
      default:
        return DeltaTypes.Neutral;
    }
  }

  // Every entry regardless of the selected period, most recent first.
  static getManualEntries(oneRepMaxes: OneRepMax[]): OneRepMaxValue[] {
    return oneRepMaxes
      .flatMap((oneRepMax) => oneRepMax.oneRepMaxes)
      .slice()
      .sort(firstBy((entry: OneRepMaxValue) => new Date(entry.createdDate).getTime(), "desc"));
  }

  static getXAxisNumTicks(spanInDays: number, width: number) {
    if (spanInDays <= 7) return width < 500 ? 4 : 7;
    if (spanInDays <= 31) return width < 750 ? 6 : 12;
    if (spanInDays <= 90) return width < 650 ? 6 : 12;
    return width < 500 ? 6 : 12;
  }
}

export default OneRepMaxChartHelpers;
